import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GIFT_FULFILMENT_LABELS,
  GIFT_REQUEST_STATUS_LABELS,
  GIFT_DETAILS_AFTER_SENT_DAYS,
} from '@affily/shared/constants';
import { formatCents } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { giftStatusTone } from '../../components/PartnershipGifts.jsx';
import { Button, Badge, Spinner, EmptyState, Alert, Field, Input } from '../../components/ui.jsx';

// What needs the brand first, then what is in flight, then history.
const GROUPS = [
  { status: 'requested', title: 'Waiting for your decision' },
  { status: 'approved', title: 'Approved — ready to send' },
  { status: 'sent', title: 'Sent' },
  { status: 'redeemed', title: 'Redeemed' },
  { status: 'declined', title: 'Declined' },
  { status: 'cancelled', title: 'Cancelled by the creator' },
];

function Details({ details }) {
  if (!details) {
    return <p className="text-xs text-ink-faint">Delivery details are no longer held.</p>;
  }
  const a = details.shippingAddress;
  return (
    <div className="rounded-md border border-rule bg-paper-raised p-3 text-sm">
      {a ? (
        <address className="not-italic leading-relaxed text-ink">
          {a.name}
          <br />
          {a.line1}
          {a.line2 && (
            <>
              <br />
              {a.line2}
            </>
          )}
          <br />
          {[a.city, a.region, a.postalCode].filter(Boolean).join(', ')}
          <br />
          {a.country}
          {a.phone && (
            <>
              <br />
              {a.phone}
            </>
          )}
        </address>
      ) : (
        <p className="whitespace-pre-line text-ink">{details.bookingNote}</p>
      )}
      <p className="mt-2 text-xs text-ink-faint">
        Only you can see this. Use it to fulfil the gift and nothing else; Affily deletes it
        automatically.
      </p>
    </div>
  );
}

function RequestCard({ r, onDone, setError }) {
  const [mode, setMode] = useState(null); // 'decline' | 'sent' | null
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function act(action, body) {
    setBusy(true);
    setError('');
    try {
      await api.post(`/gift-requests/${r.id}/${action}`, body);
      setMode(null);
      setNote('');
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="rounded-lg border border-rule bg-paper-raised p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-ink">
            {r.gift.name}
            <span className="font-normal text-ink-muted"> for </span>
            {r.creator ? (
              <Link to={`/creators/${r.creator.slug}`} className="hover:text-accent">
                {r.creator.displayName}
              </Link>
            ) : (
              'a creator'
            )}
          </p>
          <p className="text-xs text-ink-muted">
            {r.program?.title} · {GIFT_FULFILMENT_LABELS[r.gift.fulfilment]}
            {r.gift.retailValueCents != null &&
              ` · ${formatCents(r.gift.retailValueCents, { dropZeroCents: true, currency: r.program?.currency })} brand-stated value`}
            {' · asked '}
            {new Date(r.requestedAt).toLocaleDateString()}
          </p>
        </div>
        <Badge tone={giftStatusTone[r.status]}>{GIFT_REQUEST_STATUS_LABELS[r.status]}</Badge>
      </div>

      {r.status === 'requested' && r.gift.fulfilment === 'ship' && r.shipCountry && (
        <p className="mt-2 text-sm text-ink-muted">
          Ship to: <strong className="text-ink">{r.shipCountry}</strong>. The full address is shown
          once you approve.
        </p>
      )}
      {r.status === 'requested' && r.gift.fulfilment === 'booking' && (
        <p className="mt-2 text-sm text-ink-muted">Their booking details are shown once you approve.</p>
      )}
      {!r.partnershipId && r.applicationId && r.isOpen && (
        <p className="mt-2 text-xs text-ink-faint">
          Asked for with an application. Accepting the application approves this gift too, unless you
          decline it first.
        </p>
      )}

      {(r.status === 'approved' || r.status === 'sent') && (
        <div className="mt-3">
          <Details details={r.details} />
        </div>
      )}

      {r.decisionNote && <p className="mt-2 text-xs text-ink-muted">Your note: {r.decisionNote}</p>}
      {r.sentNote && <p className="mt-1 text-xs text-ink-muted">Reference: {r.sentNote}</p>}
      {r.status === 'sent' && (
        <p className="mt-1 text-xs text-ink-faint">
          Delivery details are deleted when the creator marks it received, or {GIFT_DETAILS_AFTER_SENT_DAYS}{' '}
          days after you marked it sent.
        </p>
      )}

      {r.isOpen && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {r.status === 'requested' && (
            <>
              <Button size="sm" disabled={busy} onClick={() => act('decide', { decision: 'approve' })}>
                Approve
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode('decline')}>
                Decline…
              </Button>
            </>
          )}
          {r.status === 'approved' && (
            <>
              <Button size="sm" disabled={busy} onClick={() => setMode('sent')}>
                Mark sent…
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode('decline')}>
                Can’t fulfil — decline…
              </Button>
            </>
          )}
          {r.status === 'sent' && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act('redeemed')}>
              Mark redeemed
            </Button>
          )}
        </div>
      )}

      {mode && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2 border-t border-rule pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (mode === 'decline') act('decide', { decision: 'decline', note });
            else act('sent', { note });
          }}
        >
          <Field
            label={mode === 'decline' ? 'Reason for the creator' : 'Tracking number or booking reference'}
            constraint={`${rule.optional()} · ${rule.max(300)}`}
            className="min-w-64 flex-1"
          >
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
          </Field>
          <Button type="submit" size="sm" variant={mode === 'decline' ? 'danger' : 'primary'} disabled={busy}>
            {mode === 'decline' ? 'Decline' : 'Mark sent'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setMode(null)}>
            Back
          </Button>
        </form>
      )}
    </li>
  );
}

/**
 * Gift requests for a brand: decide, send, and see what's left.
 *
 * Personal details are shown here and only here, only for approved and sent
 * requests, and are never part of any list a creator or the public can fetch.
 */
export default function BrandGifts() {
  const { data, loading, reload } = useFetch('/gift-requests?side=brand');
  const [error, setError] = useState('');

  const requests = data?.giftRequests ?? [];
  const stock = (data?.programs ?? []).flatMap((p) =>
    p.gifts.filter((g) => g.isActive).map((g) => ({ ...g, programTitle: p.title, programId: p.id })),
  );

  return (
    <DashboardLayout
      side="brand"
      title="Gifts"
      description="Requests for the gifts your programs offer. Approving a limited gift sets one unit aside for that creator."
    >
      <Alert className="mb-4">{error}</Alert>

      {loading ? (
        <Spinner />
      ) : (
        <div className="space-y-8">
          {stock.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-semibold">Stock</h2>
              <ul className="grid gap-2 sm:grid-cols-2">
                {stock.map((g) => (
                  <li
                    key={`${g.programId}-${g.key}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-rule bg-paper-raised px-3 py-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{g.name}</span>
                      <span className="block truncate text-xs text-ink-faint">{g.programTitle}</span>
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-ink-muted">
                      {g.limited ? (
                        <strong className={g.remaining === 0 ? 'text-negative' : 'text-ink'}>
                          {g.remaining} of {g.quantityTotal} left
                        </strong>
                      ) : (
                        'Unlimited'
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-ink-faint">
                Change quantities from the program editor; whatever has been approved stays counted.
              </p>
            </section>
          )}

          {requests.length === 0 ? (
            <EmptyState title="No gift requests yet">
              When a creator asks for one of your gifts, it shows up here. You decide each one; nothing
              is promised until you approve it.
            </EmptyState>
          ) : (
            GROUPS.map(({ status, title }) => {
              const rows = requests.filter((r) => r.status === status);
              if (!rows.length) return null;
              return (
                <section key={status}>
                  <h2 className="mb-2 text-sm font-semibold">
                    {title} <span className="font-normal text-ink-faint">· {rows.length}</span>
                  </h2>
                  <ul className="space-y-3">
                    {rows.map((r) => (
                      <RequestCard key={r.id} r={r} onDone={reload} setError={setError} />
                    ))}
                  </ul>
                </section>
              );
            })
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
